/**
 * Full-width loading state for a whole view.
 *
 * Used where the alternative is showing the wrong thing: the shell resolving
 * which tab to open would otherwise flash the Ask tab before switching to the
 * dashboard, and a dashboard mid-load would render an empty frame that fills in
 * widget by widget. Both read as a glitch; a loader reads as work in progress.
 *
 * Pass `percent` (0–100) when the work is countable — e.g. a dashboard running N
 * reports — to show a determinate progress bar and a live percentage, so a slow
 * multi-report load reads as advancing rather than stuck.
 */
export function PageLoader({ label, percent }: { label: string; percent?: number }): React.JSX.Element {
  const showBar = typeof percent === 'number';
  const pct = showBar ? Math.max(0, Math.min(100, Math.round(percent))) : 0;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '64px 20px',
        minHeight: 240,
      }}
    >
      <div className="loader-ring" aria-hidden="true" />
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        {label}
        {showBar ? <span style={{ marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span> : null}
      </p>
      {showBar ? (
        <div
          className="loader-bar"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="loader-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}
